/**
 * Document Detail Panel
 *
 * Slide-out panel showing governance document details with owner assignment.
 * Documents have a simpler governance model than datasets:
 * - Single baseline persona: Document Owner
 * - Red indicator = no owner, Green = owner assigned
 *
 * @version 1.0.0
 * @date 2025-12-05
 */

import React, { useState, useEffect, useCallback } from 'react';
import './document-detail-panel.css';
import { SpeakerMappingPanel } from '../../components/triangulation/SpeakerMappingPanel';
import { GroupSessionAnalysisSection } from '../../components/triangulation/GroupSessionAnalysisSection';

// =============================================================================
// TYPES
// =============================================================================

interface GovernanceDocument {
  document_id: string;
  document_name: string;
  document_type: string;
  category: string;
  version: string | null;
  maturity_score: number | null;
  completeness_status: 'complete' | 'partial' | 'missing' | null;
  fair_pillars: {
    findable: boolean;
    accessible: boolean;
    interoperable: boolean;
    reusable: boolean;
  };
  pillar_scores: {
    F: number | null;
    A: number | null;
    I: number | null;
    R: number | null;
  } | null;
  analysis_version: string | null;
  uploaded_at: string;
  analyzed_at: string | null;
}

interface Employee {
  employee_id: string;
  display_name: string;
  email: string;
  department?: string;
  job_title?: string;
  is_active: boolean;
}

interface DocumentOwnerAssignment {
  assignment_id: string;
  persona_id: string;
  persona_name: string;
  employee_id: string;
  employee_name: string;
  employee_email: string;
  department?: string;
  assigned_at: string;
  assignment_notes?: string;
}

interface DocumentGovernanceSummary {
  document_id: string;
  document_name: string;
  governance_status: 'complete' | 'missing';
  assignments: DocumentOwnerAssignment[];
  missing_owner: boolean;
}

interface DocumentDetailPanelProps {
  document: GovernanceDocument;
  onClose: () => void;
  onOwnerChange?: () => void;
}

// =============================================================================
// CATEGORY DEFINITIONS
// =============================================================================

const DOCUMENT_CATEGORIES: Record<string, { label: string; icon: string }> = {
  data_quality: { label: 'Data Quality', icon: 'Q' },
  data_access: { label: 'Data Access', icon: 'A' },
  data_lifecycle: { label: 'Data Lifecycle', icon: 'L' },
  metadata: { label: 'Metadata Standards', icon: 'M' },
  privacy: { label: 'Privacy & Consent', icon: 'P' },
  retention: { label: 'Retention Policy', icon: 'R' },
  security: { label: 'Security', icon: 'S' },
  other: { label: 'Other', icon: 'O' },
};

const DOCUMENT_TYPES: Record<string, string> = {
  policy: 'Policy',
  procedure: 'Procedure',
  guideline: 'Guideline',
  standard: 'Standard',
  other: 'Other',
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const DocumentDetailPanel: React.FC<DocumentDetailPanelProps> = ({
  document,
  onClose,
  onOwnerChange,
}) => {
  const [governanceSummary, setGovernanceSummary] = useState<DocumentGovernanceSummary | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Assignment form state
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [assignmentNotes, setAssignmentNotes] = useState('');

  // DC-1652: FAIR Findings report export
  const [exportingReport, setExportingReport] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExportFairReport = useCallback(async () => {
    setExportingReport(true);
    setExportError(null);
    try {
      const response = await fetch(
        `/api/organization/documents/${document.document_id}/fair-report?format=pdf`,
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(
          (body as { error?: { message?: string } }).error?.message ||
            `Failed to export FAIR report (HTTP ${response.status})`,
        );
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : 'FAIR_Findings.pdf';
      const url = URL.createObjectURL(blob);
      // NOTE: `document` is the component prop here — the DOM document must
      // be reached via window.
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      console.error('[DocumentDetailPanel] FAIR report export error:', err);
      setExportError(err instanceof Error ? err.message : 'Failed to export FAIR report');
    } finally {
      setExportingReport(false);
    }
  }, [document.document_id]);

  // Fetch governance summary and employees on mount
  useEffect(() => {
    fetchGovernanceSummary();
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchGovernanceSummary has document.document_id in deps
  }, [document.document_id]);

  const fetchGovernanceSummary = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/organization/documents/${document.document_id}/governance`);
      if (!response.ok) {
        throw new Error('Failed to fetch document governance');
      }

      const data = await response.json();
      if (data.success) {
        setGovernanceSummary(data.summary);
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (err: unknown) {
      console.error('[DocumentDetailPanel] Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch governance');
    } finally {
      setLoading(false);
    }
  }, [document.document_id]);

  const fetchEmployees = async () => {
    try {
      const response = await fetch('/api/employees');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setEmployees(data.employees.filter((e: Employee) => e.is_active));
        }
      }
    } catch (err) {
      console.error('[DocumentDetailPanel] Failed to fetch employees:', err);
    }
  };

  const handleAssignOwner = async () => {
    if (!selectedEmployeeId) {
      alert('Please select an employee');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`/api/organization/documents/${document.document_id}/governance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: selectedEmployeeId,
          assignment_notes: assignmentNotes || undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to assign owner');
      }

      // Reset form and refresh
      setShowAssignForm(false);
      setSelectedEmployeeId('');
      setAssignmentNotes('');
      await fetchGovernanceSummary();

      if (onOwnerChange) {
        onOwnerChange();
      }
    } catch (err: unknown) {
      console.error('[DocumentDetailPanel] Assignment error:', err);
      alert(err instanceof Error ? err.message : 'Failed to assign owner');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveOwner = async (assignmentId: string) => {
    if (!confirm('Remove this owner assignment?')) return;

    setSaving(true);
    try {
      const response = await fetch(
        `/api/organization/documents/${document.document_id}/governance/${assignmentId}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error('Failed to remove owner');
      }

      await fetchGovernanceSummary();

      if (onOwnerChange) {
        onOwnerChange();
      }
    } catch (err: unknown) {
      console.error('[DocumentDetailPanel] Remove error:', err);
      alert(err instanceof Error ? err.message : 'Failed to remove owner');
    } finally {
      setSaving(false);
    }
  };

  // Get category info
  const categoryInfo = DOCUMENT_CATEGORIES[document.category] || { label: document.category, icon: '?' };
  const documentTypeLabel = DOCUMENT_TYPES[document.document_type] || document.document_type;

  // Format date
  const formatDate = (dateStr: string): string => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const hasOwner = governanceSummary?.governance_status === 'complete';

  return (
    <div className="doc-panel-overlay" onClick={onClose}>
      <div className="doc-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="doc-panel__header">
          <div className="doc-panel__title-area">
            <div>
              <h2 className="doc-panel__title">{document.document_name}</h2>
              <div className="doc-panel__meta">
                <span className="doc-panel__type">{documentTypeLabel}</span>
                <span className="doc-panel__category">[{categoryInfo.icon}] {categoryInfo.label}</span>
                {document.version && <span className="doc-panel__version">v{document.version}</span>}
              </div>
            </div>
          </div>
          <button className="doc-panel__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {/* Content */}
        <div className="doc-panel__content">
          {/* Owner Section */}
          <section className="doc-panel__section">
            <div className="doc-panel__section-header">
              <h3>Document Owner</h3>
              <span className={`owner-status-badge ${hasOwner ? 'owner-status-badge--assigned' : 'owner-status-badge--missing'}`}>
                {hasOwner ? '● Assigned' : '○ Not Assigned'}
              </span>
            </div>

            {loading ? (
              <div className="doc-panel__loading">Loading...</div>
            ) : error ? (
              <div className="doc-panel__error">{error}</div>
            ) : (
              <>
                {/* Current assignments */}
                {governanceSummary?.assignments && governanceSummary.assignments.length > 0 ? (
                  <div className="doc-owner-list">
                    {governanceSummary.assignments.map((assignment) => (
                      <div key={assignment.assignment_id} className="doc-owner-card">
                        <div className="doc-owner-card__info">
                          <span className="doc-owner-card__name">{assignment.employee_name}</span>
                          <span className="doc-owner-card__email">{assignment.employee_email}</span>
                          {assignment.department && (
                            <span className="doc-owner-card__dept">{assignment.department}</span>
                          )}
                          <span className="doc-owner-card__date">
                            Assigned: {formatDate(assignment.assigned_at)}
                          </span>
                          {assignment.assignment_notes && (
                            <span className="doc-owner-card__notes">{assignment.assignment_notes}</span>
                          )}
                        </div>
                        <button
                          className="doc-owner-card__remove"
                          onClick={() => handleRemoveOwner(assignment.assignment_id)}
                          disabled={saving}
                          title="Remove owner"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="doc-panel__empty">
                    <p>No owner assigned to this document.</p>
                    <p className="doc-panel__empty-hint">
                      Assigning an owner ensures accountability for document maintenance and accuracy.
                    </p>
                  </div>
                )}

                {/* Add owner button */}
                {!showAssignForm && (
                  <button
                    className="doc-panel__add-btn"
                    onClick={() => setShowAssignForm(true)}
                  >
                    + Assign Owner
                  </button>
                )}

                {/* Assignment form */}
                {showAssignForm && (
                  <div className="doc-assign-form">
                    <h4>Assign Document Owner</h4>
                    <div className="doc-assign-form__field">
                      <label>Employee</label>
                      <select
                        value={selectedEmployeeId}
                        onChange={(e) => setSelectedEmployeeId(e.target.value)}
                        className="doc-assign-form__select"
                      >
                        <option value="">Select an employee...</option>
                        {employees.map((emp) => (
                          <option key={emp.employee_id} value={emp.employee_id}>
                            {emp.display_name} ({emp.email})
                            {emp.department ? ` - ${emp.department}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="doc-assign-form__field">
                      <label>Notes (optional)</label>
                      <textarea
                        value={assignmentNotes}
                        onChange={(e) => setAssignmentNotes(e.target.value)}
                        placeholder="e.g., Primary contact for this policy"
                        rows={2}
                        className="doc-assign-form__textarea"
                      />
                    </div>
                    <div className="doc-assign-form__actions">
                      <button
                        onClick={handleAssignOwner}
                        disabled={saving || !selectedEmployeeId}
                        className="btn btn--primary"
                      >
                        {saving ? 'Assigning...' : 'Assign'}
                      </button>
                      <button
                        onClick={() => {
                          setShowAssignForm(false);
                          setSelectedEmployeeId('');
                          setAssignmentNotes('');
                        }}
                        className="btn btn--secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>

          {/* Document Info Section */}
          <section className="doc-panel__section">
            <h3>Document Information</h3>
            <div className="doc-info-grid">
              <div className="doc-info-item">
                <label>Uploaded</label>
                <span>{formatDate(document.uploaded_at)}</span>
              </div>
              {document.analyzed_at && (
                <div className="doc-info-item">
                  <label>Last Analyzed</label>
                  <span>{formatDate(document.analyzed_at)}</span>
                </div>
              )}
              {document.maturity_score !== null && (
                <div className="doc-info-item">
                  <label>Maturity Score</label>
                  <span>{Math.round(document.maturity_score)}%</span>
                </div>
              )}
              {document.completeness_status && (
                <div className="doc-info-item">
                  <label>Status</label>
                  <span className={`status-tag status-tag--${document.completeness_status}`}>
                    {document.completeness_status}
                  </span>
                </div>
              )}
            </div>

            {/* FAIR Pillars */}
            <div className="doc-info-item doc-info-item--full">
              <label>FAIR Pillars Evaluated</label>
              <div className="doc-pillar-badges">
                <span className={`pillar-badge ${document.fair_pillars.findable ? 'pillar-badge--active pillar-badge--F' : 'pillar-badge--inactive'}`}>
                  F
                </span>
                <span className={`pillar-badge ${document.fair_pillars.accessible ? 'pillar-badge--active pillar-badge--A' : 'pillar-badge--inactive'}`}>
                  A
                </span>
                <span className={`pillar-badge ${document.fair_pillars.interoperable ? 'pillar-badge--active pillar-badge--I' : 'pillar-badge--inactive'}`}>
                  I
                </span>
                <span className={`pillar-badge ${document.fair_pillars.reusable ? 'pillar-badge--active pillar-badge--R' : 'pillar-badge--inactive'}`}>
                  R
                </span>
              </div>
            </div>

            {/* DC-1652: FAIR Findings report — enabled once the document has
                analyzed pillar scores. Tier 2 analyses export the full
                principle-level report; pattern-based analyses export the
                scorecard with an honest source label. */}
            {document.pillar_scores !== null && (
              <div className="doc-info-item doc-info-item--full">
                <button
                  className="btn btn--secondary"
                  onClick={handleExportFairReport}
                  disabled={exportingReport}
                  data-testid="export-fair-report-btn"
                >
                  {exportingReport ? 'Exporting…' : 'Export FAIR Report'}
                </button>
                {exportError && (
                  <p className="doc-panel__error" role="alert">{exportError}</p>
                )}
              </div>
            )}
          </section>

          {/* v2.0: session-level sentiment analysis (Analyze Session → View /
              Export anonymized PDF). Only renders for group_transcript. */}
          <GroupSessionAnalysisSection
            documentId={document.document_id}
            documentName={document.document_name}
            category={document.category}
          />

          {/* DC-1603: speaker → persona mapping. Only renders when the
              document is a group_transcript; no-op for every other category. */}
          <SpeakerMappingPanel
            documentId={document.document_id}
            category={document.category}
          />
        </div>
      </div>
    </div>
  );
};

export default DocumentDetailPanel;
